import { Address, createPublicClient, http, ReadContractReturnType } from 'viem';
import { SupportedChainId, SupportedChains } from '@packet/constants/AppSupportChains';
import { App_ABI } from '@packet/abi/App';
import { ERC20_ABI } from '@packet/abi/ERC20';
import { localCache } from './storage';
import { readContract, multicall } from 'viem/actions';

type PoolsReturn = ReadContractReturnType<typeof App_ABI, 'getPoolInfo'>;

// Token完整信息类型
interface TokenInfo {
  symbol: string;
  name: string;
  decimals: number;
}

// Pool缓存项类型
interface PoolCacheItem {
  data: PoolsReturn;
  chainId: SupportedChainId;
  assetId: string;
}

// Token信息缓存项类型
interface TokenInfoCacheItem {
  data: TokenInfo;
  chainId: SupportedChainId;
  tokenAddress: string;
}

// Pool正在进行的请求缓存
interface PoolPendingRequest {
  promise: Promise<PoolsReturn>;
  chainId: SupportedChainId;
  assetId: string;
}

// Token信息正在进行的请求缓存
interface TokenInfoPendingRequest {
  promise: Promise<TokenInfo>;
  chainId: SupportedChainId;
  tokenAddress: string;
}

class TokenPoolCache {
  private poolPendingRequests = new Map<string, PoolPendingRequest>();
  private tokenInfoPendingRequests = new Map<string, TokenInfoPendingRequest>();

  /**
   * 生成Pool缓存键
   */
  private generatePoolKey(chainId: SupportedChainId, assetId: string): string {
    return `tokenPool:${chainId}-${assetId}`;
  }

  /**
   * 生成Token Info缓存键
   */
  private generateTokenInfoKey(chainId: SupportedChainId, tokenAddress: string): string {
    return `tokenInfo:${chainId}-${tokenAddress}`;
  }

  /**
   * 从缓存中获取Pool数据
   */
  private getCachedPoolData(chainId: SupportedChainId, assetId: string): PoolsReturn | undefined {
    const key = this.generatePoolKey(chainId, assetId);
    const cached = localCache.getItem<PoolCacheItem>(key);
    if (cached && cached.chainId === chainId && cached.assetId === assetId) {
      return cached.data;
    }
    return undefined;
  }

  /**
   * 将Pool数据保存到缓存
   */
  private setCachedPoolData(chainId: SupportedChainId, assetId: string, data: PoolsReturn): void {
    const key = this.generatePoolKey(chainId, assetId);
    const cacheItem: PoolCacheItem = {
      data,
      chainId,
      assetId,
    };
    localCache.setItem(key, cacheItem);
  }

  /**
   * 从缓存中获取Token Info数据
   */
  private getCachedTokenInfoData(chainId: SupportedChainId, tokenAddress: string): TokenInfo | undefined {
    const key = this.generateTokenInfoKey(chainId, tokenAddress);
    const cached = localCache.getItem<TokenInfoCacheItem>(key);
    if (cached && cached.chainId === chainId && cached.tokenAddress === tokenAddress) {
      return cached.data;
    }
    return undefined;
  }

  /**
   * 将Token Info数据保存到缓存
   */
  private setCachedTokenInfoData(chainId: SupportedChainId, tokenAddress: string, data: TokenInfo): void {
    const key = this.generateTokenInfoKey(chainId, tokenAddress);
    const cacheItem: TokenInfoCacheItem = {
      data,
      chainId,
      tokenAddress,
    };
    localCache.setItem(key, cacheItem);
  }

  /**
   * 从合约获取pool数据
   */
  private async fetchPoolFromContract(contractAddress: Address, chainId: SupportedChainId, assetId: string): Promise<PoolsReturn> {
    const chain = SupportedChains.find((chain) => chain.id === chainId);
    if (!chain) throw new Error(`Unsupported chainId: ${chainId}`);
    const publicClient = createPublicClient({ chain, transport: http() });
    try {
      const result = await readContract(publicClient, {
        address: contractAddress,
        abi: App_ABI,
        functionName: 'getPoolInfo',
        args: [BigInt(assetId)],
      });
      console.log('fetchPoolFromContract', result);
      return result;
    } catch (error) {
      console.error(`Failed to fetch pool data for chainId: ${chainId}, assetId: ${assetId}`, error);
      throw new Error(`Failed to fetch pool data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * 使用viem multicall从合约获取token完整信息
   */
  private async fetchTokenInfoFromContract(tokenAddress: Address, chainId: SupportedChainId): Promise<TokenInfo> {
    const chain = SupportedChains.find((chain) => chain.id === chainId);
    if (!chain) throw new Error(`Unsupported chainId: ${chainId}`);
    const publicClient = createPublicClient({ chain, transport: http() });

    try {
      // 使用 viem 的 multicall
      const results = await multicall(publicClient, {
        contracts: [
          {
            address: tokenAddress,
            abi: ERC20_ABI,
            functionName: 'symbol',
          },
          {
            address: tokenAddress,
            abi: ERC20_ABI,
            functionName: 'name',
          },
          {
            address: tokenAddress,
            abi: ERC20_ABI,
            functionName: 'decimals',
          },
        ],
      });

      const tokenInfo: TokenInfo = {
        symbol: results[0].result as string,
        name: results[1].result as string,
        decimals: Number(results[2].result),
      };

      console.log('fetchTokenInfoFromContract', tokenInfo);
      return tokenInfo;
    } catch (error) {
      console.error(`Failed to fetch token info for chainId: ${chainId}, tokenAddress: ${tokenAddress}`, error);
      throw new Error(`Failed to fetch token info: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * 获取pool数据（带缓存和防重复请求）
   */
  async getPool(contractAddress: Address, chainId: SupportedChainId, assetId: string): Promise<PoolsReturn> {
    const key = this.generatePoolKey(chainId, assetId);

    // 检查缓存
    const cachedData = this.getCachedPoolData(chainId, assetId);
    if (cachedData) {
      console.log(`Cache hit for pool chainId: ${chainId}, assetId: ${assetId}`);
      return cachedData;
    }

    // 检查是否有正在进行的请求
    const pendingRequest = this.poolPendingRequests.get(key);
    if (pendingRequest && pendingRequest.chainId === chainId && pendingRequest.assetId === assetId) {
      console.log(`Reusing pending pool request for chainId: ${chainId}, assetId: ${assetId}`);
      return await pendingRequest.promise;
    }

    // 创建新的请求
    console.log(`Fetching new pool data for chainId: ${chainId}, assetId: ${assetId}`);
    const promise = this.fetchPoolFromContract(contractAddress, chainId, assetId);

    // 缓存待处理的请求
    this.poolPendingRequests.set(key, {
      promise,
      chainId,
      assetId,
    });

    try {
      const poolData = await promise;

      // 成功获取数据后，更新缓存
      this.setCachedPoolData(chainId, assetId, poolData);

      // 清理待处理请求
      this.poolPendingRequests.delete(key);

      return poolData;
    } catch (error) {
      // 请求失败时清理待处理请求
      this.poolPendingRequests.delete(key);
      throw error;
    }
  }

  /**
   * 获取token完整信息（带缓存和防重复请求）
   */
  async getTokenInfo(tokenAddress: Address, chainId: SupportedChainId): Promise<TokenInfo> {
    // 检查是否为原生代币
    const isNativeToken = tokenAddress === '0x0000000000000000000000000000000000000000' || tokenAddress === '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE';

    if (isNativeToken) {
      const chain = SupportedChains.find((c) => c.id === chainId);
      if (!chain) throw new Error(`Unsupported chainId: ${chainId}`);
      return {
        symbol: chain.nativeCurrency.symbol,
        name: chain.nativeCurrency.name,
        decimals: chain.nativeCurrency.decimals,
      };
    }

    const key = this.generateTokenInfoKey(chainId, tokenAddress);

    // 检查缓存
    const cachedData = this.getCachedTokenInfoData(chainId, tokenAddress);
    if (cachedData) {
      console.log(`Cache hit for token info chainId: ${chainId}, tokenAddress: ${tokenAddress}`);
      return cachedData;
    }

    // 检查是否有正在进行的请求
    const pendingRequest = this.tokenInfoPendingRequests.get(key);
    if (pendingRequest && pendingRequest.chainId === chainId && pendingRequest.tokenAddress === tokenAddress) {
      console.log(`Reusing pending token info request for chainId: ${chainId}, tokenAddress: ${tokenAddress}`);
      return await pendingRequest.promise;
    }

    // 创建新的请求
    console.log(`Fetching new token info data for chainId: ${chainId}, tokenAddress: ${tokenAddress}`);
    const promise = this.fetchTokenInfoFromContract(tokenAddress, chainId);

    // 缓存待处理的请求
    this.tokenInfoPendingRequests.set(key, {
      promise,
      chainId,
      tokenAddress,
    });

    try {
      const tokenInfo = await promise;

      // 成功获取数据后，更新缓存
      this.setCachedTokenInfoData(chainId, tokenAddress, tokenInfo);

      // 清理待处理请求
      this.tokenInfoPendingRequests.delete(key);

      return tokenInfo;
    } catch (error) {
      // 请求失败时清理待处理请求
      this.tokenInfoPendingRequests.delete(key);
      throw error;
    }
  }
}

// 导出单例实例
export const tokenPoolCache = new TokenPoolCache();
